import { match } from 'ts-pattern'
import type { RefreshRequestStateResponse, RefreshTriggerSourceResponse } from '../../utils/api-client/api-schemas.ts'
import * as styles from './refresh-state-badge.css.ts'

type BadgeVariant = 'pending' | 'regenerating' | 'completed' | 'failed' | 'no-revision' | 'superseded'

const getVariant = (state: RefreshRequestStateResponse): BadgeVariant =>
  match(state)
    .with('pending', 'claimed', () => 'pending' as const)
    .with('regenerating', () => 'regenerating' as const)
    .with('completed', () => 'completed' as const)
    .with('failed', () => 'failed' as const)
    .with('no-revision', () => 'no-revision' as const)
    .with('superseded', () => 'superseded' as const)
    .exhaustive()

const getLabel = (
  state: RefreshRequestStateResponse,
  triggerSource: RefreshTriggerSourceResponse | undefined
): string =>
  match(state)
    .with('pending', () => 'Pending')
    .with('claimed', () => 'Evaluating')
    // "Regenerating" implies a prior model; onboarding rows are the initial generation, so we label
    // them "Generating" instead. Variant stays the same — the visual meaning ("work in progress")
    // is unchanged.
    .with('regenerating', () => (triggerSource === 'onboarding' ? 'Generating' : 'Regenerating'))
    .with('completed', () => 'Completed')
    .with('failed', () => 'Failed')
    .with('no-revision', () => 'No update needed')
    .with('superseded', () => 'Superseded')
    .exhaustive()

type RefreshStateBadgeProps = {
  state: RefreshRequestStateResponse
  triggerSource?: RefreshTriggerSourceResponse
}

export const RefreshStateBadge: React.FC<RefreshStateBadgeProps> = ({ state, triggerSource }) => (
  <span className={styles.badge({ variant: getVariant(state) })}>{getLabel(state, triggerSource)}</span>
)
